import { distributionSpans, normalizeDesktopColumnLayout } from 'src/helpers/columnDistribution';
import { assignBlockSortOrders, reindexBlocks } from 'src/helpers/pageContentUtils';
import {
  ColumnAlignH,
  ColumnsDesktopLayout,
  IColumnsBreakpointOrder,
  IColumnsLayout,
  IColumnsPerRow,
  IPageColumn,
  OrphanRowPolicy,
} from 'src/interfaces/PageColumn';
import { HeroMobileStackOrder } from 'src/interfaces/HeroBlock';

const MIN_COLUMNS = 2;
const MAX_COLUMNS = 4;

function emptyColumn(alignH: ColumnAlignH = 'center'): IPageColumn {
  return { alignH, alignV: 'center', blocks: [] };
}

export function defaultColumnsPerRow(columnCount: number): IColumnsPerRow {
  const n = Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, columnCount));
  if (n === 2) return { xs: 1, sm: 2, md: 2 };
  if (n === 3) return { xs: 1, sm: 2, md: 3 };
  return { xs: 1, sm: 2, md: 4 };
}

export function defaultMobileColumnOrder(columnCount: number): number[] {
  return Array.from({ length: columnCount }, (_, i) => i);
}

export function createColumnsLayout(columnCount: 2 | 3 | 4): IColumnsLayout {
  const count = Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, columnCount));
  return {
    columns: Array.from({ length: count }, () => emptyColumn()),
    mobileColumnOrder: defaultMobileColumnOrder(count),
    columnsPerRow: defaultColumnsPerRow(count),
    orphanRowPolicy: 'natural',
  };
}

function clampPerRow(value: number, max: number): number {
  const v = Math.floor(value);
  if (!Number.isFinite(v) || v < 1) return 1;
  return Math.min(max, v);
}

export function normalizeColumnsPerRow(
  columnCount: number,
  raw?: Partial<IColumnsPerRow>,
): IColumnsPerRow {
  const defaults = defaultColumnsPerRow(columnCount);
  const n = columnCount;
  return {
    xs: clampPerRow(raw?.xs ?? defaults.xs, n),
    sm: clampPerRow(raw?.sm ?? defaults.sm, n),
    md: clampPerRow(raw?.md ?? defaults.md, n),
  };
}

function isValidPermutation(order: number[] | undefined, n: number): order is number[] {
  if (!order?.length || order.length !== n) return false;
  const seen = new Set<number>();
  for (const i of order) {
    if (!Number.isInteger(i) || i < 0 || i >= n || seen.has(i)) return false;
    seen.add(i);
  }
  return seen.size === n;
}

export function mobileOrderFromLegacyStack(
  stackOrder: HeroMobileStackOrder | undefined,
  columnCount: number,
): number[] {
  if (columnCount === 2 && stackOrder === 'column2First') {
    return [1, 0];
  }
  return defaultMobileColumnOrder(columnCount);
}

export function stackOrderFromMobileOrder(order: number[]): HeroMobileStackOrder {
  if (order.length === 2 && order[0] === 1 && order[1] === 0) {
    return 'column2First';
  }
  return 'column1First';
}

export function normalizeColumnsLayout(
  layout: IColumnsLayout | undefined,
  options?: {
    legacyMobileStackOrder?: HeroMobileStackOrder;
    minColumns?: number;
    maxColumns?: number;
  },
): IColumnsLayout | undefined {
  if (!layout?.columns?.length) return undefined;

  const minCols = options?.minColumns ?? MIN_COLUMNS;
  const maxCols = options?.maxColumns ?? MAX_COLUMNS;
  let cols = layout.columns.slice(0, maxCols);

  while (cols.length < minCols) {
    cols = [...cols, emptyColumn()];
  }

  const n = cols.length;
  const legacyOrder = options?.legacyMobileStackOrder;
  let mobileColumnOrder = layout.mobileColumnOrder;
  if (!isValidPermutation(mobileColumnOrder, n)) {
    mobileColumnOrder = mobileOrderFromLegacyStack(legacyOrder, n);
  }

  const normalizedColumns: IPageColumn[] = cols.map(col => ({
    alignH: col.alignH ?? 'center',
    alignV: col.alignV ?? 'center',
    blocks: reindexBlocks(col.blocks ?? []),
  }));

  const orphanRowPolicy: OrphanRowPolicy =
    layout.orphanRowPolicy === 'fullWidth' || layout.orphanRowPolicy === 'centered'
      ? layout.orphanRowPolicy
      : 'natural';

  const fallbackOrder = isValidPermutation(mobileColumnOrder, n)
    ? mobileColumnOrder
    : undefined;
  const columnOrder: IColumnsBreakpointOrder = {
    xs: isValidPermutation(layout.columnOrder?.xs, n)
      ? layout.columnOrder!.xs
      : fallbackOrder ?? defaultMobileColumnOrder(n),
    sm: isValidPermutation(layout.columnOrder?.sm, n)
      ? layout.columnOrder!.sm
      : fallbackOrder ?? defaultMobileColumnOrder(n),
    md: isValidPermutation(layout.columnOrder?.md, n)
      ? layout.columnOrder!.md
      : defaultMobileColumnOrder(n),
  };

  const columnsPerRow = normalizeColumnsPerRow(n, layout.columnsPerRow);
  const desktopColumnLayout = normalizeDesktopColumnLayout(n, layout.desktopColumnLayout);

  return {
    columns: normalizedColumns,
    mobileColumnOrder: columnOrder.xs,
    columnOrder,
    columnsPerRow,
    desktopColumnLayout,
    orphanRowPolicy,
  };
}

export function orderColumnsForDisplay(
  columns: IPageColumn[],
  order?: number[],
): IPageColumn[] {
  const n = columns.length;
  const effectiveOrder = isValidPermutation(order, n)
    ? order
    : defaultMobileColumnOrder(n);
  return effectiveOrder.map(i => columns[i]!);
}

export function gridSpanForColumnsPerRow(perRow: number): number | null {
  if (!Number.isInteger(perRow) || perRow < 1 || 12 % perRow !== 0) return null;
  return 12 / perRow;
}

export function isOrphanRow(columnCount: number, perRow: number): boolean {
  if (perRow < 1 || columnCount <= perRow) return false;
  return columnCount % perRow !== 0;
}

export function hasOrphanAtAnyBreakpoint(
  columnCount: number,
  columnsPerRow: IColumnsPerRow,
): boolean {
  return (
    isOrphanRow(columnCount, columnsPerRow.xs) ||
    isOrphanRow(columnCount, columnsPerRow.sm) ||
    isOrphanRow(columnCount, columnsPerRow.md)
  );
}

/** True when the last column sits alone on a partial row at the given per-row count. */
export function isLastColumnOrphanAtBreakpoint(
  columnIndex: number,
  columnCount: number,
  perRow: number,
): boolean {
  if (columnIndex !== columnCount - 1) return false;
  if (!isOrphanRow(columnCount, perRow)) return false;
  return (columnIndex + 1) % perRow !== 0;
}

type ColumnsBreakpoint = keyof IColumnsPerRow;

const COLUMN_BREAKPOINTS: ColumnsBreakpoint[] = ['xs', 'sm', 'md'];

/** Build mobile-first sx values that reset once orphan layout no longer applies. */
function mobileFirstOrphanToggle(
  orphanByBp: Record<ColumnsBreakpoint, boolean>,
  onValue: string,
  offValue: string,
): Partial<Record<ColumnsBreakpoint, string>> {
  const result: Partial<Record<ColumnsBreakpoint, string>> = {};
  let orphanActive = false;

  for (const bp of COLUMN_BREAKPOINTS) {
    if (orphanByBp[bp]) {
      result[bp] = onValue;
      orphanActive = true;
    } else if (orphanActive) {
      result[bp] = offValue;
      orphanActive = false;
    }
  }

  return result;
}

function lastColumnOrphanByBreakpoint(
  columnIndex: number,
  columnCount: number,
  perRow: IColumnsPerRow,
): Record<ColumnsBreakpoint, boolean> {
  return {
    xs: isLastColumnOrphanAtBreakpoint(columnIndex, columnCount, perRow.xs),
    sm: isLastColumnOrphanAtBreakpoint(columnIndex, columnCount, perRow.sm),
    md: isLastColumnOrphanAtBreakpoint(columnIndex, columnCount, perRow.md),
  };
}

/** Container justify overrides when centering a lone orphan column on its row. */
export function orphanCenteredContainerSx(
  layout: IColumnsLayout,
): { justifyContent: Partial<Record<ColumnsBreakpoint, string>> } | undefined {
  if ((layout.orphanRowPolicy ?? 'natural') !== 'centered') return undefined;

  const columnCount = layout.columns.length;
  const orphanByBp = lastColumnOrphanByBreakpoint(columnCount - 1, columnCount, layout.columnsPerRow!);
  const justifyContent = mobileFirstOrphanToggle(orphanByBp, 'center', 'flex-start');

  if (Object.keys(justifyContent).length === 0) return undefined;
  return { justifyContent };
}

export function findBlockColumn(layout: IColumnsLayout, blockId: string): number | null {
  const idx = layout.columns.findIndex(col => col.blocks.some(b => b.id === blockId));
  return idx >= 0 ? idx : null;
}

export function moveLayoutBlock<T extends IColumnsLayout>(
  layout: T,
  activeBlockId: string,
  overId: string,
  droppablePrefix: string,
): T {
  const sourceCol = findBlockColumn(layout, activeBlockId);
  if (sourceCol === null) return layout;

  let targetCol = sourceCol;
  let targetIndex: number | null = null;

  for (let i = 0; i < layout.columns.length; i++) {
    const droppableId = `${droppablePrefix}-col-${i}`;
    if (overId === droppableId) {
      targetCol = i;
      targetIndex = layout.columns[i]!.blocks.length;
      break;
    }
  }

  if (targetIndex === null) {
    const overCol = findBlockColumn(layout, overId);
    if (overCol !== null) {
      targetCol = overCol;
      targetIndex = layout.columns[overCol]!.blocks.findIndex(b => b.id === overId);
    }
  }

  if (targetIndex === null || targetIndex < 0) return layout;

  const columns = layout.columns.map(col => ({
    ...col,
    blocks: [...col.blocks],
  }));

  const sourceBlocks = columns[sourceCol]!.blocks;
  const activeIndex = sourceBlocks.findIndex(b => b.id === activeBlockId);
  if (activeIndex < 0) return layout;
  if (sourceCol === targetCol && activeIndex === targetIndex) return layout;

  const [moved] = sourceBlocks.splice(activeIndex, 1);
  if (!moved) return layout;

  columns[targetCol]!.blocks.splice(targetIndex, 0, moved);

  return {
    ...layout,
    columns: columns.map(col => ({
      ...col,
      blocks: assignBlockSortOrders(col.blocks),
    })),
  };
}

export function moveColumnOrder<T extends IColumnsLayout>(
  layout: T,
  fromIndex: number,
  toIndex: number,
): T {
  const n = layout.columns.length;
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= n || toIndex >= n) {
    return layout;
  }

  const columns = [...layout.columns];
  const [moved] = columns.splice(fromIndex, 1);
  if (!moved) return layout;
  columns.splice(toIndex, 0, moved);

  const remap = (idx: number): number => {
    if (idx === fromIndex) return toIndex;
    if (fromIndex < toIndex && idx > fromIndex && idx <= toIndex) return idx - 1;
    if (fromIndex > toIndex && idx >= toIndex && idx < fromIndex) return idx + 1;
    return idx;
  };
  const remapOrder = (order: number[] | undefined) => {
    const base = isValidPermutation(order, n) ? [...order] : defaultMobileColumnOrder(n);
    return base.map(remap);
  };
  const columnOrder = {
    xs: remapOrder(layout.columnOrder?.xs ?? layout.mobileColumnOrder),
    sm: remapOrder(layout.columnOrder?.sm ?? layout.mobileColumnOrder),
    md: remapOrder(layout.columnOrder?.md),
  };

  return {
    ...layout,
    columns,
    columnOrder,
    mobileColumnOrder: columnOrder.xs,
  };
}

export function resizeColumnsLayout(
  layout: IColumnsLayout,
  newCount: 2 | 3 | 4,
): IColumnsLayout {
  const current = layout.columns;
  let columns: IPageColumn[];
  if (newCount > current.length) {
    columns = [
      ...current,
      ...Array.from({ length: newCount - current.length }, () => emptyColumn()),
    ];
  } else {
    columns = current.slice(0, newCount);
  }

  const n = columns.length;
  let mobileColumnOrder = layout.mobileColumnOrder?.filter(i => i < n) ?? [];
  const used = new Set(mobileColumnOrder);
  for (let i = 0; i < n; i++) {
    if (!used.has(i)) mobileColumnOrder.push(i);
  }
  if (!isValidPermutation(mobileColumnOrder, n)) {
    mobileColumnOrder = defaultMobileColumnOrder(n);
  }

  return normalizeColumnsLayout({
    ...layout,
    columns,
    mobileColumnOrder,
    columnsPerRow: normalizeColumnsPerRow(n, layout.columnsPerRow),
  })!;
}

function getColumnOrderForBreakpoint(
  layout: IColumnsLayout,
  bp: keyof IColumnsBreakpointOrder,
): number[] {
  const n = layout.columns.length;
  const order = layout.columnOrder?.[bp];
  if (isValidPermutation(order, n)) return order;
  if (bp !== 'md' && isValidPermutation(layout.mobileColumnOrder, n)) {
    return layout.mobileColumnOrder!;
  }
  return defaultMobileColumnOrder(n);
}

export function getColumnCssOrder(
  layout: IColumnsLayout,
  originalIndex: number,
): { xs: number; sm: number; md: number } {
  return {
    xs: getColumnOrderForBreakpoint(layout, 'xs').indexOf(originalIndex),
    sm: getColumnOrderForBreakpoint(layout, 'sm').indexOf(originalIndex),
    md: getColumnOrderForBreakpoint(layout, 'md').indexOf(originalIndex),
  };
}

export function getColumnSpanForBreakpoint(
  layout: IColumnsLayout,
  originalIndex: number,
  bp: keyof IColumnsPerRow,
): number {
  const perRow = layout.columnsPerRow![bp];
  const n = layout.columns.length;

  if (
    (layout.orphanRowPolicy ?? 'natural') === 'fullWidth' &&
    isLastColumnOrphanAtBreakpoint(originalIndex, n, perRow)
  ) {
    return 12;
  }

  if (bp === 'md' && perRow === n) {
    const spans = distributionSpans(n, layout.desktopColumnLayout);
    if (spans) {
      const displayPos = getColumnOrderForBreakpoint(layout, 'md').indexOf(originalIndex);
      return spans[displayPos] ?? gridSpanForColumnsPerRow(perRow) ?? 12;
    }
  }
  return gridSpanForColumnsPerRow(perRow) ?? 12;
}

/** Editor select value: per-row count, optionally reversed display order. */
export type BreakpointRowConfigValue = `${number}` | `${number}-reversed`;

export function encodeBreakpointRowConfig(perRow: number, reversed: boolean): BreakpointRowConfigValue {
  return (reversed ? `${perRow}-reversed` : `${perRow}`) as BreakpointRowConfigValue;
}

export function decodeBreakpointRowConfig(
  value: string,
  columnCount: number,
): { perRow: number; order: number[] } {
  const reversed = value.endsWith('-reversed');
  const perRow = clampPerRow(Number.parseInt(value, 10), columnCount);
  const natural = defaultMobileColumnOrder(columnCount);
  const order = reversed ? [...natural].reverse() : natural;
  return { perRow, order };
}

export type DesktopRowConfigValue = `${number}` | `${number}-wide`;

function desktopConfigToColumnCount(value: DesktopRowConfigValue): 2 | 3 | 4 {
  if (value === '3-wide') return 3;
  return Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, Number.parseInt(value, 10))) as 2 | 3 | 4;
}

export function decodeDesktopRowConfig(value: string): {
  columnCount: 2 | 3 | 4;
  perRow: number;
  desktopColumnLayout: ColumnsDesktopLayout;
} {
  if (value === '3-wide') {
    return { columnCount: 3, perRow: 3, desktopColumnLayout: 'wideCenter' };
  }
  const columnCount = desktopConfigToColumnCount(value as DesktopRowConfigValue);
  return { columnCount, perRow: columnCount, desktopColumnLayout: 'equal' };
}

export function readBreakpointRowConfig(
  layout: IColumnsLayout,
  bp: keyof IColumnsBreakpointOrder,
): BreakpointRowConfigValue {
  const perRow = layout.columnsPerRow![bp === 'xs' ? 'xs' : bp === 'sm' ? 'sm' : 'md'];
  const order = getColumnOrderForBreakpoint(layout, bp);
  const natural = defaultMobileColumnOrder(layout.columns.length);
  const reversed =
    order.length === natural.length && order.every((v, i) => v === natural[natural.length - 1 - i]);
  return encodeBreakpointRowConfig(perRow, reversed);
}

export function readDesktopRowConfig(layout: IColumnsLayout): DesktopRowConfigValue {
  const n = layout.columns.length;
  if (
    n === 3 &&
    layout.desktopColumnLayout === 'wideCenter' &&
    layout.columnsPerRow!.md === 3
  ) {
    return '3-wide';
  }
  return `${Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, n))}` as DesktopRowConfigValue;
}
